import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { API_BASE } from './config';
import { getFirebaseAuth, isFirebaseConfigured } from './firebase-client';

let confirmationResult = null;
let recaptchaVerifier = null;

function normalizePhone(phone) {
  return String(phone).replace(/\D/g, '').slice(-10);
}

export function resetRecaptcha(containerId = 'firebase-recaptcha') {
  try {
    recaptchaVerifier?.clear?.();
  } catch (_) { /* ignore */ }
  recaptchaVerifier = null;
  confirmationResult = null;
  const el = document.getElementById(containerId);
  if (el) el.innerHTML = '';
}

export function ensureRecaptcha(containerId = 'firebase-recaptcha') {
  const auth = getFirebaseAuth();
  if (!auth) return null;
  if (recaptchaVerifier) return recaptchaVerifier;
  recaptchaVerifier = new RecaptchaVerifier(auth, containerId, { size: 'invisible' });
  return recaptchaVerifier;
}

async function postJson(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.error) {
    return { success: false, error: json.error || `HTTP ${res.status}` };
  }
  return { success: true, ...json };
}

/** Send OTP to email only (signup or forgot). */
export async function sendEmailOtp(purpose, email) {
  const target = String(email || '').trim().toLowerCase();
  if (!target.includes('@')) {
    return { success: false, error: 'Enter a valid email address' };
  }
  const result = await postJson('/auth/otp/email/send', { purpose, email: target });
  if (!result.success) return result;
  if (result.demo && result.otp) {
    console.log(`📧 DEMO email OTP [${purpose}] → ${target}: ${result.otp}`);
  }
  return { success: true, channel: 'email', demo: result.demo, otp: result.otp };
}

/** Verify email OTP; returns verification token for signup or reset. */
export async function verifyEmailOtp(purpose, email, otp) {
  const result = await postJson('/auth/otp/email/verify', {
    purpose,
    email: String(email || '').trim().toLowerCase(),
    otp: String(otp || '').trim(),
  });
  if (!result.success) return result;
  return { success: true, token: result.token };
}

/** Send OTP to mobile only (signup or forgot). Firebase or server demo. */
export async function sendPhoneOtp(purpose, phone, containerId = 'firebase-recaptcha') {
  const digits = normalizePhone(phone);
  if (digits.length !== 10) {
    return { success: false, error: 'Enter a valid 10-digit mobile number' };
  }

  if (!isFirebaseConfigured()) {
    const result = await postJson('/auth/otp/phone/send-demo', { purpose, phone: digits });
    if (!result.success) return result;
    if (result.demo && result.otp) {
      console.log(`📱 DEMO phone OTP [${purpose}] → ${digits}: ${result.otp}`);
    }
    return { success: true, channel: 'phone', demo: true, otp: result.otp };
  }

  try {
    const auth = getFirebaseAuth();
    ensureRecaptcha(containerId);
    confirmationResult = await signInWithPhoneNumber(auth, `+91${digits}`, recaptchaVerifier);
    return { success: true, channel: 'phone' };
  } catch (e) {
    console.error('Firebase OTP send error:', e);
    resetRecaptcha(containerId);
    return { success: false, error: e.message || 'Failed to send OTP' };
  }
}

/** Verify mobile OTP; returns verification token for signup or reset. */
export async function verifyPhoneOtp(purpose, phone, enteredOtp, containerId = 'firebase-recaptcha') {
  const digits = normalizePhone(phone);

  if (!isFirebaseConfigured()) {
    const result = await postJson('/auth/otp/phone/verify-demo', {
      purpose,
      phone: digits,
      otp: String(enteredOtp || '').trim(),
    });
    if (!result.success) return result;
    return { success: true, token: result.token };
  }

  if (!confirmationResult) {
    return { success: false, error: 'Request an OTP first.' };
  }

  try {
    const cred = await confirmationResult.confirm(String(enteredOtp || '').trim());
    const idToken = await cred.user.getIdToken();
    const result = await postJson('/auth/otp/phone/verify', {
      purpose,
      idToken,
      phone: digits,
    });
    resetRecaptcha(containerId);
    confirmationResult = null;
    if (!result.success) return result;
    return { success: true, token: result.token };
  } catch (e) {
    return { success: false, error: e.message || 'Invalid OTP' };
  }
}

export const OTPService = {
  sendEmail: sendEmailOtp,
  verifyEmail: verifyEmailOtp,
  sendPhone: sendPhoneOtp,
  verifyPhone: verifyPhoneOtp,
  resetRecaptcha,
};
